import * as React from "react";

import { PdfSourcePreview } from "../../../components/PdfSourcePreview";
import { SimpleMarkdown } from "../../../components/SimpleMarkdown";
import { getCitationColor } from "../colors";
import { getCitationRouteInfo } from "../citationSourceRouting";
import type { DocumentSourceItem } from "../../ai/sourceItems";
import {
  resolveCitationDocumentUrl,
  resolveCitationPdfUrl,
} from "../../../services/gcpFileUrl";
import { EmbeddedIframePreview } from "./EmbeddedIframePreview";
import { HtmlSourcePreview } from "./HtmlSourcePreview";
import { PdfBackedDocumentRenderer } from "./PdfBackedDocumentRenderer";
import {
  SOURCE_TABLE_CLASS_NAME,
  SOURCE_TD_CLASS_NAME,
  SOURCE_TH_CLASS_NAME,
} from "./sourceTableClasses";
import { VisitSourceButton } from "./VisitSourceButton";

export const DocumentSourceRenderer: React.FC<{
  item: DocumentSourceItem;
  highlightUuid: string | null;
  allowIframeSameOrigin?: boolean;
}> = ({ item, highlightUuid, allowIframeSameOrigin = true }) => {
  const routeInfo = getCitationRouteInfo(item);
  const resolvedLink = resolveCitationDocumentUrl(routeInfo.rawLink);
  const linkText = item.link.text ?? "";
  const activeCitationId = highlightUuid ?? null;
  const citationColor = getCitationColor(
    routeInfo.dataSource ?? item.label ?? item.client ?? null
  );

  switch (routeInfo.route) {
    case "contentWithPdf":
      return (
        <PdfBackedDocumentRenderer
          item={item}
          content={linkText}
          pdfSrc={resolveCitationPdfUrl(routeInfo.rawLink)}
          highlightUuid={activeCitationId}
          citationColor={citationColor}
          hasStructuredHtml={routeInfo.hasStructuredHtml}
        />
      );
    case "privatePdf":
      if (!resolvedLink) {
        return (
          <p className="text-[12px] text-muted-foreground">No source URL was provided for this PDF.</p>
        );
      }
      return (
        <PdfSourcePreview
          href={resolveCitationPdfUrl(routeInfo.rawLink)}
          title={item.link.metadata?.documentName ?? item.link.link}
          highlights={item.link.highlights}
          initialHighlightId={activeCitationId}
        />
      );
    case "privateDocument":
      if (!resolvedLink) {
        return (
          <p className="text-[12px] text-muted-foreground">
            No source URL was provided for this document.
          </p>
        );
      }
      return (
        <DocumentIframe
          src={resolvedLink}
          title="Source document preview"
          allowSameOrigin={allowIframeSameOrigin}
        />
      );
    case "iframeHtml":
      return (
        <EmbeddedIframePreview
          html={linkText}
          highlightUuid={activeCitationId}
          citationColor={citationColor}
          allowSameOrigin={allowIframeSameOrigin}
        />
      );
    case "html":
      if (!routeInfo.hasStructuredHtml) {
        return (
          <MarkdownDocumentBody
            markdown={linkText}
            resolvedLink={resolvedLink}
            highlightUuid={activeCitationId}
          />
        );
      }
      return (
        <HtmlDocumentBody
          html={linkText}
          resolvedLink={resolvedLink}
          highlightUuid={activeCitationId}
          title={item.link.metadata?.documentName ?? ""}
          sourceName={item.link.metadata?.companyName}
        />
      );
    case "markdown":
      return (
        <MarkdownDocumentBody
          markdown={linkText}
          resolvedLink={resolvedLink}
          highlightUuid={activeCitationId}
        />
      );
  }

  return null;
};

const CITATION_SCROLL_MAX_ATTEMPTS = 24;
const CITATION_TARGET_FLASH_MS = 2600;

type CitationTargetHandler = (args: {
  container: HTMLDivElement;
  target: HTMLElement;
}) => void | (() => void);

const useCitationTargetScroller = ({
  hasContent,
  highlightUuid,
  contentKey,
  onTarget,
}: {
  hasContent: boolean;
  highlightUuid: string | null;
  contentKey: string;
  onTarget: CitationTargetHandler;
}) => {
  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!hasContent || !highlightUuid) return undefined;

    let frameId = 0;
    let targetCleanup: (() => void) | undefined;
    let attempts = 0;

    const scrollToMarker = () => {
      const container = scrollContainerRef.current;
      if (!container) return;
      const targets = Array.from(container.querySelectorAll<HTMLElement>("[data-citation-id]"));
      const target = targets.find((element) => element.dataset.citationId === highlightUuid);
      if (!target) {
        attempts += 1;
        if (attempts < CITATION_SCROLL_MAX_ATTEMPTS) {
          frameId = window.requestAnimationFrame(scrollToMarker);
        }
        return;
      }

      const cleanup = onTarget({ container, target });
      if (cleanup) {
        targetCleanup = cleanup;
      }
    };

    frameId = window.requestAnimationFrame(scrollToMarker);

    return () => {
      window.cancelAnimationFrame(frameId);
      targetCleanup?.();
    };
  }, [hasContent, highlightUuid, contentKey, onTarget]);

  return scrollContainerRef;
};

const HtmlDocumentBody: React.FC<{
  html: string;
  resolvedLink: string;
  highlightUuid: string | null;
  title: string;
  sourceName?: string;
}> = ({ html, resolvedLink, highlightUuid, title, sourceName }) => {
  const hasContent = html.length > 0;
  const [flashCitationId, setFlashCitationId] = React.useState<string | null>(highlightUuid);

  const [previousTarget, setPreviousTarget] = React.useState({ highlightUuid, html });
  if (previousTarget.highlightUuid !== highlightUuid || previousTarget.html !== html) {
    setPreviousTarget({ highlightUuid, html });
    setFlashCitationId(highlightUuid);
  }

  const handleCitationTarget = React.useCallback<CitationTargetHandler>(({ container, target }) => {
    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    container.scrollTo({
      top: container.scrollTop + targetRect.top - containerRect.top - 120,
      behavior: "smooth",
    });
    const timeoutId = window.setTimeout(() => setFlashCitationId(null), CITATION_TARGET_FLASH_MS);
    return () => window.clearTimeout(timeoutId);
  }, []);

  const scrollContainerRef = useCitationTargetScroller({
    hasContent,
    highlightUuid,
    contentKey: html,
    onTarget: handleCitationTarget,
  });

  return (
    <section className="flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
      {hasContent ? (
        <>
          <div
            ref={scrollContainerRef}
            className="factset-scrollbar min-h-0 flex-1 overflow-auto bg-background px-1 py-5"
          >
            <article className="prose-citation citation-html-preview citation-html-article citation-source-preview--compact mx-auto max-w-[720px] bg-background">
              <HtmlSourcePreview
                html={html}
                activeCitationId={flashCitationId}
                title={title}
                sourceName={sourceName}
                filterSourceChrome
              />
            </article>
          </div>
          <div className="flex shrink-0 items-center border-t border-border bg-background px-1 py-3">
            <VisitSourceButton href={resolvedLink} />
          </div>
        </>
      ) : (
        <div className="flex min-h-0 flex-1 items-center justify-center px-4 py-10">
          <p className="text-[12px] text-muted-foreground">No content snippet returned for this source.</p>
        </div>
      )}
    </section>
  );
};

const MarkdownDocumentBody: React.FC<{
  markdown: string;
  resolvedLink: string;
  highlightUuid: string | null;
}> = ({ markdown, resolvedLink, highlightUuid }) => {
  const hasContent = markdown.length > 0;

  const handleCitationTarget = React.useCallback<CitationTargetHandler>(({ target }) => {
    target.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
    target.classList.add("citation-html-active-target");
    const timeoutId = window.setTimeout(() => {
      target.classList.remove("citation-html-active-target");
    }, CITATION_TARGET_FLASH_MS);
    return () => window.clearTimeout(timeoutId);
  }, []);

  const scrollContainerRef = useCitationTargetScroller({
    hasContent,
    highlightUuid,
    contentKey: markdown,
    onTarget: handleCitationTarget,
  });

  return (
    <section className="flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
      {hasContent ? (
        <>
          <div
            ref={scrollContainerRef}
            className="factset-scrollbar min-h-0 flex-1 overflow-auto bg-background px-1 py-5"
          >
            <article className="prose-citation citation-markdown-preview citation-source-preview--compact mx-auto max-w-[720px] bg-background">
              <SimpleMarkdown
                text={markdown}
                className="citation-markdown-content"
                tableClassName={SOURCE_TABLE_CLASS_NAME}
                thClassName={SOURCE_TH_CLASS_NAME}
                tdClassName={SOURCE_TD_CLASS_NAME}
                compact
                activeCitationId={highlightUuid}
              />
            </article>
          </div>
          <div className="flex shrink-0 items-center border-t border-border bg-background px-1 py-3">
            <VisitSourceButton href={resolvedLink} />
          </div>
        </>
      ) : (
        <div className="flex min-h-0 flex-1 items-center justify-center px-4 py-10">
          <p className="text-[12px] text-muted-foreground">No content snippet returned for this source.</p>
        </div>
      )}
    </section>
  );
};

const DocumentIframe: React.FC<{
  src: string;
  title: string;
  allowSameOrigin: boolean;
}> = ({ src, title, allowSameOrigin }) => (
  <div className="overflow-hidden rounded-md border border-border bg-muted/20">
    <iframe
      title={title}
      src={src}
      referrerPolicy="no-referrer"
      sandbox={`allow-scripts${allowSameOrigin ? " allow-same-origin" : ""} allow-popups allow-downloads`}
      style={{ width: "100%", height: 640, border: "0", display: "block" }}
    />
  </div>
);

